import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, CheckCircle2, Loader2 } from "lucide-react";
import { Reveal, Kicker } from "./Reveal";
import { COMPANY, SERVICE_OPTIONS } from "../../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", phone: "", email: "", service_type: "", message: "" };

const validate = (form) => {
  const errors = {};
  if (form.name.trim().length < 2) errors.name = "Please enter your full name.";
  if (!/^[\d\s()+.-]{7,}$/.test(form.phone.trim()))
    errors.phone = "Please enter a valid phone number.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim()))
    errors.email = "Please enter a valid email address.";
  if (!form.service_type) errors.service_type = "Please select a service.";
  if (form.message.trim().length < 10)
    errors.message = "Tell us a little more about your project (min. 10 characters).";
  return errors;
};

const inputCls =
  "w-full rounded-none border border-black/15 bg-white px-4 py-3.5 text-sm text-ink placeholder:text-black/30 transition-colors duration-300 focus:border-bronze focus:outline-none focus:ring-1 focus:ring-bronze";

const Field = ({ label, error, testid, children }) => (
  <div>
    <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.25em] text-black/50">
      {label}
    </label>
    {children}
    {error && (
      <p data-testid={`${testid}-error`} className="mt-1.5 text-xs text-bronze">
        {error}
      </p>
    )}
  </div>
);

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    const onPrefill = (e) => {
      setForm((f) => ({ ...f, service_type: e.detail }));
      setStatus("idle");
    };
    window.addEventListener("prefill-service", onPrefill);
    return () => window.removeEventListener("prefill-service", onPrefill);
  }, []);

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    if (errors[key]) setErrors({ ...errors, [key]: undefined });
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/quotes`, form);
      setStatus("success");
      toast.success("Quote request sent — we'll be in touch within one working day.");
    } catch (err) {
      setStatus("idle");
      toast.error("Something went wrong sending your enquiry. Please try again or call us directly.");
    }
  };

  const details = [
    { icon: Phone, label: "Call us", value: COMPANY.phone, href: COMPANY.phoneHref },
    { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
    { icon: MapPin, label: "Registered Office", value: COMPANY.office },
    { icon: Clock, label: "Working Hours", value: COMPANY.hours },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-36">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-5 lg:gap-20">
        <div className="lg:col-span-2">
          <Reveal>
            <Kicker index="09" label="Contact" />
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-5xl font-bold uppercase leading-[0.95] tracking-tight text-ink md:text-6xl">
              Let's Build
              <span className="block text-gold-shine">Something Solid</span>
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-8 max-w-md leading-relaxed text-fog">
              Tell us about your project and we'll come back with a clear,
              itemised quotation — usually within one working day. No pressure,
              no obligation.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-10 space-y-6 border-t border-black/10 pt-10">
              {details.map((item) => {
                const Icon = item.icon;
                const inner = (
                  <>
                    <span className="grid h-11 w-11 shrink-0 place-items-center border border-bronze/40">
                      <Icon className="h-5 w-5 text-bronze" strokeWidth={1.8} />
                    </span>
                    <span>
                      <span className="block text-[11px] uppercase tracking-[0.25em] text-black/50">
                        {item.label}
                      </span>
                      <span className="mt-1 block text-sm text-ink/85">{item.value}</span>
                    </span>
                  </>
                );
                return item.href ? (
                  <a
                    key={item.label}
                    data-testid={`contact-detail-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    href={item.href}
                    className="flex items-center gap-4 transition-colors duration-300 hover:text-bronze"
                  >
                    {inner}
                  </a>
                ) : (
                  <div
                    key={item.label}
                    data-testid={`contact-detail-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex items-center gap-4"
                  >
                    {inner}
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-3">
          <div className="border border-black/10 bg-coal p-8 md:p-12">
            {status === "success" ? (
              <div data-testid="quote-success" className="flex flex-col items-start gap-6 py-10">
                <span className="grid h-16 w-16 place-items-center border border-bronze/50">
                  <CheckCircle2 className="h-8 w-8 text-bronze" strokeWidth={1.5} />
                </span>
                <h3 className="font-display text-4xl font-bold uppercase tracking-wide text-ink">
                  Enquiry Received
                </h3>
                <p className="max-w-md leading-relaxed text-fog">
                  Thank you, {form.name.split(" ")[0]}. Your quote request has
                  been logged and a member of our team will contact you within
                  one working day.
                </p>
                <button
                  data-testid="quote-another-btn"
                  onClick={() => {
                    setForm(EMPTY);
                    setStatus("idle");
                  }}
                  className="border border-black/25 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:border-bronze hover:text-bronze"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form data-testid="quote-form" onSubmit={submit} noValidate>
                <div className="grid gap-6 sm:grid-cols-2">
                  <Field label="Full Name *" error={errors.name} testid="quote-name">
                    <input
                      data-testid="quote-name-input"
                      type="text"
                      value={form.name}
                      onChange={set("name")}
                      placeholder="James Whitfield"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Phone *" error={errors.phone} testid="quote-phone">
                    <input
                      data-testid="quote-phone-input"
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="07700 900000"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Email *" error={errors.email} testid="quote-email">
                    <input
                      data-testid="quote-email-input"
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@example.co.uk"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Service Required *" error={errors.service_type} testid="quote-service">
                    <select
                      data-testid="quote-service-select"
                      value={form.service_type}
                      onChange={set("service_type")}
                      className={`${inputCls} appearance-none ${form.service_type ? "" : "text-black/40"}`}
                    >
                      <option value="" disabled>
                        Select a service
                      </option>
                      {SERVICE_OPTIONS.map((option) => (
                        <option key={option} value={option} className="bg-white text-ink">
                          {option}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Project Details *" error={errors.message} testid="quote-message">
                      <textarea
                        data-testid="quote-message-input"
                        rows={5}
                        value={form.message}
                        onChange={set("message")}
                        placeholder="Tell us about your project — location, scope, timescales…"
                        className={`${inputCls} resize-none`}
                      />
                    </Field>
                  </div>
                </div>
                <button
                  data-testid="quote-submit-btn"
                  type="submit"
                  disabled={status === "sending"}
                  className="mt-8 flex w-full items-center justify-center gap-3 bg-bronze px-9 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-ink hover:text-bronze disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {status === "sending" ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending
                    </>
                  ) : (
                    "Request My Quote"
                  )}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
